import { useMemo } from 'react';
import { Link } from 'react-router';
import { TenantModel } from '../../../models/TenantModel';
import { Table, columnDefiner, TableRow } from '../../atomic/table';
import { reformat } from '../../../utils/DateUtils';
import { ConfigIcon, UserIcon } from '../../atomic/icons';
import { useTenantEditDialog } from './TenantEdit';
import { useUpdateTenantDialog } from './TenantFormDialog';
import { comma } from '../../../utils/FormUtils';
import { DownloadSummaries } from './DownloadSummaries';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '../../atomic/buttons';
import { useTenantAdminUserDialog } from './useTenantAdminUserDialog';

interface TenantRow extends TableRow {
    tenant: TenantModel;
    openFormModal: () => void;
    openOptionModal: () => void;
    openAdminUserModal: () => void;
}

const definer = columnDefiner<TenantRow>();

const columns = [
    definer.accessor('tenant.tenantId', {
        header: 'テナントID',
        cell: (d) => {
            const { tenantId } = d.row.original.tenant;

            return (
                <>
                    <div className="tenant id">
                        <span>{tenantId}</span>
                    </div>
                </>
            );
        },
    }),
    {
        header: 'テナント名',
        cell: (d) => {
            const {
                tenant: { name, upload, deleted },
                openFormModal,
            } = d.row.original;
            return (
                <>
                    <Link to={`#`} onClick={openFormModal}>
                        {name} {!!!upload && <>{'(アップロード不可)'}</>}
                        {deleted && <>(削除済み)</>}
                    </Link>
                </>
            );
        },
    },
    {
        header: 'ストレージ(使用/最大)',
        cell: (d) => {
            const { availableStorageSize, size, tenantId } =
                d.row.original.tenant;
            return (
                <>
                    <Link to={`/app/superadmin/tenant/${tenantId}/usages`}>
                        {`${comma(size)} GB/${comma(availableStorageSize)} GB`}
                    </Link>
                </>
            );
        },
    },
    {
        header: 'ストレージ利用率(%)',
        cell: (d) => {
            const { usage, tenantId } = d.row.original.tenant;
            return (
                <Link to={`/app/superadmin/tenant/${tenantId}/usages`}>
                    {usage}
                </Link>
            );
        },
    },
    {
        header: 'ストレージ容量算出日',
        cell: (d) => {
            const { finishedAt } = d.row.original.tenant;
            return `${reformat(finishedAt)}`;
        },
    },
    {
        header: 'ユーザー数',
        cell: (d) => {
            const { numberOfUsers } = d.row.original.tenant;
            return `${numberOfUsers}`;
        },
    },
    {
        header: '最大ユーザー数',
        cell: (d) => {
            const { maxUserLicenses } = d.row.original.tenant;
            return `${maxUserLicenses}`;
        },
    },
    {
        header: 'AI実行許可',
        cell: (d) => {
            const { aiRequest } = d.row.original.tenant;
            return aiRequest ? 'はい' : 'いいえ';
        },
    },
    {
        header: 'AI実行枚数',
        cell: (d) => {
            const { aiRequestCount } = d.row.original.tenant;
            return `${comma(aiRequestCount)}`;
        },
    },
    {
        header: 'AI実行許可枚数',
        cell: (d) => {
            const { maxAiRequest } = d.row.original.tenant;
            return `${maxAiRequest ? comma(maxAiRequest) : '無制限'}`;
        },
    },
    {
        header: '履歴ダウンロード',
        cell: (d) => {
            const tenant = d.row.original.tenant;
            return <DownloadSummaries tenant={tenant} />;
        },
    },
    {
        header: '更新日',
        cell: (d) => {
            const { updatedAt } = d.row.original.tenant;
            return `${reformat(updatedAt)}`;
        },
    },
    {
        header: '作成日',
        cell: (d) => {
            const { createdAt } = d.row.original.tenant;
            return `${reformat(createdAt)}`;
        },
    },
    {
        header: 'オプション',
        cell: (d) => {
            const { openOptionModal, openAdminUserModal } = d.row.original;
            return (
                <>
                    <Button icon="fa-solid fa-gear" onClick={openOptionModal} />
                    <Button
                        icon="fa-solid fa-users"
                        onClick={openAdminUserModal}
                    />
                </>
            );
        },
    },
] satisfies ColumnDef<TenantRow, any>[];

export type TenantListProps = {
    tenants: TenantModel[];
};

export const TenantList = ({ tenants }: TenantListProps) => {
    const tenantUpdate = useUpdateTenantDialog();
    const tenantEdit = useTenantEditDialog();
    const tenantAdminUser = useTenantAdminUserDialog({}, []);

    const data = useMemo(
        () =>
            tenants.map(
                (tenant): TenantRow => ({
                    tenant,
                    openFormModal: () => tenantUpdate.show(tenant),
                    openOptionModal: () => tenantEdit.show(tenant),
                    openAdminUserModal: () => tenantAdminUser.show({ tenant }),
                })
            ),
        [tenants]
    );

    return (
        <>
            <div className="edit-forms">
                {tenants.length ? (
                    <Table
                        className="tenant-table"
                        data={data}
                        columns={columns}
                        options={{
                            getRowId: (row) => row.tenant.tenantId,
                        }}
                    />
                ) : (
                    ''
                )}
            </div>
            {tenantUpdate.dialog}
            {tenantEdit.dialog}
            {tenantAdminUser.dialog}
        </>
    );
};
